import { Zeno } from "../types/mash";
import * as constants from "./constants";

type WebSocketMessage<T> = {
  type: "subscribe" | "unsubscribe" | "ping" | "pong";
  channel?: keyof ChannelDataTypes;
  status?: "ok";
  data?: T;
};

export type SocketEventUpdateMessage = WebSocketMessage<Zeno.EventUpdate>;
export type SocketEventAddedMessage = WebSocketMessage<Zeno.EventAdded>;
export type SocketEventRemovedMessage = WebSocketMessage<Zeno.EventRemoved>;
export type SocketMarketUpdateMessage = WebSocketMessage<Zeno.MarketUpdate>;
export type SocketChartMetaDataMessage = WebSocketMessage<Zeno.ChartMetaData>;

export type SocketSubscribeMessage = WebSocketMessage<{
  type: "subscribe";
  status: "ok";
}>;
export type SocketUnsubscribeMessage = WebSocketMessage<{
  type: "unsubscribe";
  status: "ok";
}>;

export type SocketMessage =
  | SocketEventUpdateMessage
  | SocketEventAddedMessage
  | SocketEventRemovedMessage
  | SocketMarketUpdateMessage
  | SocketChartMetaDataMessage
  | SocketSubscribeMessage
  | SocketUnsubscribeMessage;
// Define supported channel types and their data types
interface ChannelDataTypes {
  "event:update": Zeno.EventUpdate;
  "event:added": Zeno.EventAdded;
  "event:removed": Zeno.EventRemoved;
  // Add more channel types here as needed
  "market:updated": Zeno.MarketUpdate;
  "market:chart:meta": Zeno.ChartMetaData;
  // 'trade:update': TradeData
}

class WebSocketService {
  public socket: WebSocket | null = null;
  private static instance: WebSocketService;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private baseReconnectDelay = 1000;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private pongTimeout: NodeJS.Timeout | null = null;
  private messageQueue: string[] = [];
  private messageHandlers: Set<(event: MessageEvent) => void> = new Set();
  private defaultChannels: Array<keyof ChannelDataTypes> = [
    "event:update",
    "event:added",
    "event:removed",
    "market:updated",
  ];

  private constructor() {
    // Add visibility change listener only once
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible" && !this.socket) {
        this.initialize();
      }
    });

    // Add online/offline listener only once
    window.addEventListener("online", () => {
      if (!this.socket) {
        this.initialize();
      }
    });
  }

  static getInstance() {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  private subscribeToChannels() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;

    this.defaultChannels.forEach((channel) => {
      this.socket!.send(
        JSON.stringify({
          type: "subscribe",
          channel,
        }),
      );
    });
  }

  private getReconnectDelay(): number {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s
    return Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      16000, // Max delay of 16 seconds
    );
  }

  private reconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(
        "Max reconnection attempts reached. Please refresh the page.",
      );
      return;
    }

    const delay = this.getReconnectDelay();
    console.log(
      `Attempting to reconnect in ${delay}ms... (Attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`,
    );

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.initialize();
    }, delay);
  }

  private startPingInterval() {
    this.pingInterval = setInterval(() => {
      this.sendPing();
    }, 15000); // Send ping every 15 seconds
  }

  private sendPing() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: "ping" }));

      // Set a timeout for pong response
      this.pongTimeout = setTimeout(() => {
        console.warn("No pong received, reconnecting...");
        this.socket?.close();
      }, 5000); // Wait 5 seconds for pong
    }
  }

  private handlePong() {
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  private clearIntervals() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  private processMessageQueue() {
    while (
      this.messageQueue.length > 0 &&
      this.socket?.readyState === WebSocket.OPEN
    ) {
      const message = this.messageQueue.shift();
      if (message) {
        this.socket.send(message);
      }
    }
  }

  public send(message: string) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(message);
    } else {
      this.messageQueue.push(message);
    }
  }

  public addEventListener(handler: (event: MessageEvent) => void) {
    this.messageHandlers.add(handler);

    // Auto-initialize if not already connected or connecting
    if (
      !this.socket ||
      (this.socket.readyState !== WebSocket.OPEN &&
        this.socket.readyState !== WebSocket.CONNECTING)
    ) {
      this.initialize();
    } else if (this.socket) {
      this.socket.addEventListener("message", handler);
    }
  }

  public removeEventListener(handler: (event: MessageEvent) => void) {
    this.messageHandlers.delete(handler);
    if (this.socket) {
      this.socket.removeEventListener("message", handler);
    }
  }

  private handleMessage = (event: MessageEvent) => {
    const data = JSON.parse(event.data);

    if (data.type === "pong") {
      this.handlePong();
      return;
    }

    if (data.type === "ping") {
      this.send(JSON.stringify({ type: "pong" }));
      return;
    }

    // Broadcast message to all handlers
    this.messageHandlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        console.error("Error in message handler:", error);
      }
    });
  };

  initialize() {
    if (
      this.socket?.readyState === WebSocket.OPEN ||
      this.socket?.readyState === WebSocket.CONNECTING
    )
      return;

    this.socket = new WebSocket(constants.Zeno.wsUrl);

    this.socket.onopen = () => {
      console.log("WebSocket connection established");
      this.reconnectAttempts = 0;
      this.subscribeToChannels();
      this.startPingInterval();
      this.processMessageQueue();

      // Clear any existing listeners first to prevent duplicates
      if (this.socket) {
        this.socket.onmessage = null;
        // Set the main message handler (this will broadcast to all registered handlers)
        this.socket.onmessage = this.handleMessage;
      }
    };

    this.socket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    this.socket.onclose = (event) => {
      console.log(
        `WebSocket closed with code ${event.code}. Reason: ${JSON.stringify(event)}`,
      );
      this.clearIntervals();
      this.socket = null;
      this.reconnect();
    };
  }

  cleanup() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    this.clearIntervals();
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.reconnectAttempts = 0;
    this.messageQueue = [];
  }
}

export const ws = WebSocketService.getInstance();
